import { Component, inject } from '@angular/core';
import { MediaBlock } from '../media-block/media-block';
import { InformationBlock } from '../information-block/information-block';
import { CreateContentService } from '../../services/create-content.service';
import { RecommendationBlock } from '../recommendation-block/recommendation-block';
import { PublicationBlock } from '../publication-block/publication-block';
import { FormGroup, FormControl, Validators, FormControlStatus } from '@angular/forms';
import { PlanningType } from '../../types/planning.type';
import { VisibilityType } from '../../types/visibility.type';
import { SpeedMode } from '../../types/speed-mode.type';
import { AudioMode } from '../../types/audio-mode.type';
import { Coordinate } from '@kinomap-dev/ng-ui-lib/core';
import { Observable, Subscription } from 'rxjs';
import { scheduledValidator } from '../../utils/scheduled.validator';

@Component({
  selector: 'app-creation-scenic-form',
  imports: [MediaBlock, InformationBlock, RecommendationBlock, PublicationBlock],
  templateUrl: './creation-scenic-form.html',
  styleUrl: './creation-scenic-form.scss',
})
export class CreationScenicForm {
  private createContentService: CreateContentService = inject(CreateContentService);

  scenicForm: FormGroup = new FormGroup({
    mediaBlock: new FormGroup({
      videoFile: new FormControl<File | null>(null, Validators.required),
      coordinates: new FormControl<Coordinate[] | null>(null, [
        Validators.required,
        Validators.minLength(15),
      ]),
      thumbnailTimestamp: new FormControl<number | null>(null),
      syncroTime: new FormControl<number | null>(null, Validators.required),
    }),
    informationBlock: new FormGroup({
      title: new FormControl<string | null>(null, [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(128),
      ]),
      description: new FormControl<string | null>(null, [Validators.maxLength(256)]),
      recordedWith: new FormControl<string | null>(null, [Validators.maxLength(256)]),
      vehiculeId: new FormControl<number | null>(null, Validators.required),
      tagsIds: new FormControl<number[] | null>(null),
    }),
    recommandationBlock: new FormGroup({
      speedMode: new FormControl<null | SpeedMode>('none', Validators.required),
      audioMode: new FormControl<null | AudioMode>('none', Validators.required),
    }),
    publicationBlock: new FormGroup(
      {
        visibility: new FormControl<null | VisibilityType>('public', Validators.required),
        planning: new FormControl<null | PlanningType>('immediate', Validators.required),
        planningDate: new FormControl<Date | null>(null),
        planningTime: new FormControl<string | null>(null),
      },
      { validators: scheduledValidator },
    ),
  });
  subscriptions: Subscription[] = [];

  get mediaBlockFormGroup(): FormGroup {
    return this.scenicForm.get('mediaBlock') as FormGroup;
  }

  get publicationBlockFormGroup(): FormGroup {
    return this.scenicForm.get('publicationBlock') as FormGroup;
  }

  get recommandationBlockFormGroup(): FormGroup {
    return this.scenicForm.get('recommandationBlock') as FormGroup;
  }

  get informationBlockFormGroup(): FormGroup {
    return this.scenicForm.get('informationBlock') as FormGroup;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.scenicForm.statusChanges.subscribe((status: FormControlStatus) => {
        if (status === 'VALID') {
          this.createContentService.scenicFormIsValid.set(true);
        } else {
          this.createContentService.scenicFormIsValid.set(false);
        }
      }),
      this.scenicForm.get('informationBlock')!.valueChanges.subscribe((values) => {
        this.createContentService.title.set(values.title ?? undefined);
        this.createContentService.description.set(values.description ?? '');
        this.createContentService.vehiculeId.set(values.vehiculeId);
        this.createContentService.recordedWith.set(values.recordedWith ?? '');
      }),
      this.scenicForm.get('recommandationBlock')!.valueChanges.subscribe((values) => {
        this.createContentService.speedMode.set(values.speedMode);
        this.createContentService.audioMode.set(values.audioMode);
      }),
      this.scenicForm.get('publicationBlock')!.valueChanges.subscribe((values) => {
        this.createContentService.updatePublication(values);
      }),
    );

    const localSave: string | null = localStorage.getItem('scenic_form');
    if (localSave) this.scenicForm.patchValue(JSON.parse(localSave));
    this.createContentService.restoreForm(this.scenicForm, 'scenic');

    const infoBlock = this.scenicForm.get('informationBlock')!;
    this.createContentService.title.set(infoBlock.get('title')!.value ?? undefined);
    this.createContentService.description.set(infoBlock.get('description')!.value ?? '');
    this.createContentService.vehiculeId.set(infoBlock.get('vehiculeId')!.value);
    this.createContentService.recordedWith.set(infoBlock.get('recordedWith')!.value ?? '');

    const recBlock = this.scenicForm.get('recommandationBlock')!;
    this.createContentService.speedMode.set(recBlock.get('speedMode')!.value);
    this.createContentService.audioMode.set(recBlock.get('audioMode')!.value);

    const pubBlock = this.scenicForm.get('publicationBlock')!;
    this.createContentService.updatePublication(pubBlock.value);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  markAllErrors(): void {
    this.scenicForm.markAllAsTouched();
  }

  canDeactivate(): Observable<boolean> {
    return this.createContentService.canDeactivate(this.scenicForm, 'scenic');
  }
}
